import { Injectable } from '@angular/core';
import { resolveDepositEuro } from '../../utility/deposit';
import { daysFromToday } from '../../utility/share';
import { EventTotals, eventTotals } from '../../utility/totals';

/**
 * Stato condiviso della scheda evento, con scope al componente
 * EventDetailComponent (dichiarato nei suoi `providers`). Il padre e i
 * tab-figli iniettano la stessa istanza: `event` è l'unica fonte di verità.
 */
@Injectable()
export class EventDetailStore {
  event: any;
  eventId: string | null = null;
  editMode = false;
  isCollaborator = false;
  snapshot: any = null;
  // Arrivo dalla creazione con "Genera contratto": il tab Contratto avvia l'anteprima
  autoPreview = false;

  takeSnapshot(): void {
    this.snapshot = this.event ? JSON.parse(JSON.stringify(this.event)) : null;
  }

  canSee(section: string): boolean {
    if (!this.isCollaborator) return true;
    return this.event?._visibility?.collaborator?.[section] ?? false;
  }

  get clientFullName(): string {
    const b = this.event?.client;
    if (!b) return '';
    return [b.name, b.lastname].filter(Boolean).join(' ');
  }

  // ── COSTI ─────────────────────────────────────────────────────────────────
  get selectedServices(): any[] {
    return (this.event?.event?.services ?? []).filter((s: any) => s.selected);
  }

  get totalServices(): number {
    return this.selectedServices.reduce(
      (sum: number, s: any) => sum + (+s.price || 0) * (+s.quantity || 1),
      0,
    );
  }

  get testCost(): number {
    return +this.event?.event?.test_cost || 0;
  }

  get travelFee(): number {
    return +this.event?.financial?.travel_fee?.cost || 0;
  }

  get totals(): EventTotals {
    return eventTotals(this.event);
  }

  /** Totale da pagare, al netto dello sconto. */
  get totalAll(): number {
    return this.totals.total;
  }

  get discountEuro(): number {
    return this.totals.discount;
  }

  // ── PAGAMENTI ───────────────────────────────────────────────────────────────
  get depositCost(): number {
    return resolveDepositEuro(this.event?.financial?.deposit, this.totalAll);
  }

  get depositPaid(): boolean {
    return !!this.event?.financial?.deposit?.paid;
  }

  get balancePaid(): boolean {
    return !!this.event?.financial?.balance?.paid;
  }

  /** Saldo = totale − acconto (l'acconto è un anticipo sul totale). */
  get saldo(): number {
    return Math.max(0, this.totalAll - this.depositCost);
  }

  get incassato(): number {
    return (
      (this.depositPaid ? this.depositCost : 0) +
      (this.balancePaid ? this.saldo : 0)
    );
  }

  get daIncassare(): number {
    return Math.max(0, this.totalAll - this.incassato);
  }

  get balanceDueDays(): number | null {
    return daysFromToday(this.event?.financial?.balance?.due_date);
  }

  get balanceOverdue(): boolean {
    const days = this.balanceDueDays;
    return !this.balancePaid && this.saldo > 0 && days !== null && days < 0;
  }
}
